import { useEffect, useState } from "react";
import { FormattedMessage, useIntl, IntlShape } from "react-intl";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { classNames } from "helpers/commonHelpers";
import Card from "components/v2/Card/Card";
import EmptyState from "components/v2/EmptyState/EmptyState";
import { ReactComponent as ClipboardCheck } from "assets/images/iconsV2/clipboard-check-grey.svg";
import { ReactComponent as InvoiceIcon } from "assets/images/iconsV2/updates-invoice.svg";
import { ReactComponent as ShipmentIcon } from "assets/images/iconsV2/updates-shipment.svg";
import { ReactComponent as QuoteRequestIcon } from "assets/images/iconsV2/updates-quote.svg";
import { ReactComponent as NotificationIcon } from "assets/images/iconsV2/notification-text.svg";
import { ReactComponent as SearchIcon } from "assets/images/iconsV2/Search.svg";
import { ReactComponent as CheckDoneIcon } from "assets/images/iconsV2/check-done-01.svg";
import { ReactComponent as LinkExternalIcon } from "assets/images/iconsV2/link-external-01.svg";
import { ReactComponent as ClearIcon } from "assets/images/iconsV2/x-circle-grey.svg";
import { ReactComponent as FileQuestionIcon } from "assets/images/iconsV2/file-question-01.svg";
import IconCircle from "components/v2/IconCircle/IconCircle";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import Badge from "components/v2/Badge/Badge";
import Button from "components/v2/Button/Button";
import Checkbox from "components/v2/Checkbox/Checkbox";
import Input from "components/v2/Input/Input";
import Tabs from "components/v2/Tabs/Tabs";
import Tab from "components/v2/Tabs/components/Tab";
import errorToast from "helpers/errorToast";
import useGetRecentUpdatesData, { RecentUpdateNotification } from "api/queries/user/useGetRecentUpdatesData";
import { useMarkAllNotificationsAsRead, useMarkNotificationAsRead } from "api/queries/user/useMarkNotificationAsRead";

import { highlightMatchingText } from "./highlightMatchingText";
import "./RecentUpdatesWidget.scss";

interface Option {
  label: string;
  value: string;
}

interface RecentUpdatesViewState {
  activeTab: string;
  showUnreadOnly: boolean;
  searchQuery: string;
}

const defaultViewState: RecentUpdatesViewState = {
  activeTab: "all",
  showUnreadOnly: false,
  searchQuery: "",
};

// Kept in module scope so the view survives in-app navigation but resets on a browser refresh.
let retainedViewState = defaultViewState;

export const resetRecentUpdatesViewState = () => {
  retainedViewState = defaultViewState;
};

const tabOptions = (intl: IntlShape): Option[] => [
  {
    label: intl.formatMessage({
      id: "general.all",
      defaultMessage: "All",
    }),
    value: "all",
  },
  {
    label: intl.formatMessage({
      id: "general.quoteRequests",
      defaultMessage: "Quote requests",
    }),
    value: "quote_request",
  },
  {
    label: intl.formatMessage({
      id: "general.shipments",
      defaultMessage: "Shipments",
    }),
    value: "shipment",
  },
  {
    label: intl.formatMessage({
      id: "general.invoices",
      defaultMessage: "Invoices",
    }),
    value: "invoice",
  },
];

const renderIcon = (type: string) => {
  switch (type) {
    case "quote_request":
      return QuoteRequestIcon;
    case "shipment":
      return ShipmentIcon;
    case "invoice":
      return InvoiceIcon;
    default:
      return QuoteRequestIcon;
  }
};

const isUnread = (notification: RecentUpdateNotification) =>
  notification.unread ?? (notification.is_read === undefined ? false : !notification.is_read);

const getUpdatePath = (item: RecentUpdateNotification) => {
  const reference = item.type === "invoice" ? item.shipment_reference : item.reference;
  if (!reference) return "";

  const routes: Record<RecentUpdateNotification["type"], string> = {
    quote_request: `/shipment/${reference}`,
    shipment: `/shipment/${reference}/tracking`,
    quotation: `/shipment/${reference}/tracking`,
    invoice: `/shipment/${reference}/billing`,
  };

  return routes[item.type];
};

const formatRelativeTime = (created: string, now: number, intl: IntlShape) => {
  const createdAt = Date.parse(created);
  if (Number.isNaN(createdAt)) return "-";

  const elapsedSeconds = Math.max(0, Math.floor((now - createdAt) / 1000));
  if (elapsedSeconds < 60) {
    return intl.formatMessage({
      id: "dashboard.widgets.updates.justNow",
      defaultMessage: "Just now",
    });
  }
  if (elapsedSeconds < 60 * 60) return `${Math.floor(elapsedSeconds / 60)}m`;
  if (elapsedSeconds < 24 * 60 * 60) return `${Math.floor(elapsedSeconds / (60 * 60))}h`;
  return `${Math.floor(elapsedSeconds / (24 * 60 * 60))}d`;
};

function RecentUpdatesWidget() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { mutateAsync: markNotificationAsRead, isLoading: isMarkingAsRead } = useMarkNotificationAsRead();
  const { mutateAsync: markAllNotificationsAsRead, isLoading: isMarkingAllAsRead } = useMarkAllNotificationsAsRead();
  const { data: notifications, isLoading } = useGetRecentUpdatesData({
    onError: (err) =>
      errorToast(
        err,
        intl.formatMessage({
          id: "dashboard.recentUpdatesWidget.couldNotRetrieveRecentUpdates",
          defaultMessage: "Could not retrieve recent updates. Please try again later.",
        }),
      ),
  });

  const [viewState, setViewState] = useState<RecentUpdatesViewState>(() => retainedViewState);
  const [now, setNow] = useState(Date.now());

  const { activeTab, showUnreadOnly, searchQuery } = viewState;

  useEffect(() => {
    const intervalId = window.setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => window.clearInterval(intervalId);
  }, []);

  const updateViewState = (nextState: Partial<RecentUpdatesViewState>) => {
    setViewState((currentState) => {
      retainedViewState = { ...currentState, ...nextState };
      return retainedViewState;
    });
  };

  const normalizedSearch = searchQuery.trim().toLocaleLowerCase();
  const trimmedSearchQuery = searchQuery.trim();
  const filteredNotifications = (notifications ?? [])
    .filter(
      (item) =>
        (activeTab === "all" || item.type === activeTab) &&
        (!showUnreadOnly || isUnread(item)) &&
        (!normalizedSearch ||
          [item.notification, item.shipment_reference, item.reference, item.reference_title].some((value) =>
            value?.toLocaleLowerCase().includes(normalizedSearch),
          )),
    )
    .sort((a, b) => Date.parse(b.created) - Date.parse(a.created));

  const hasLoadedUpdates = notifications !== undefined;
  const hasUpdates = (notifications?.length ?? 0) > 0;
  const showSkeleton = isLoading && !hasLoadedUpdates;
  const hasActiveFilters = Boolean(trimmedSearchQuery) || showUnreadOnly || activeTab !== "all";
  const hasUnreadUpdates = (notifications ?? []).some(isUnread);

  const showMarkReadError = (error: unknown) =>
    errorToast(
      error,
      intl.formatMessage({
        id: "dashboard.widgets.updates.couldNotMarkAsRead",
        defaultMessage: "Could not mark updates as read. Please try again.",
      }),
    );

  const markAsRead = (notificationId: number) => {
    markNotificationAsRead(notificationId).catch(showMarkReadError);
  };

  const markAllAsRead = () => {
    markAllNotificationsAsRead().catch(showMarkReadError);
  };

  const handleCurrentTabNavigation = (event: React.MouseEvent<HTMLAnchorElement>, item: RecentUpdateNotification) => {
    const path = getUpdatePath(item);
    if (!path) {
      event.preventDefault();
      return;
    }

    markAsRead(item.id);
    if (!event.ctrlKey && !event.metaKey) {
      event.preventDefault();
      navigate(path);
    }
  };

  const markAllAsReadLabel = intl.formatMessage({
    id: "dashboard.widgets.updates.markAllAsRead",
    defaultMessage: "Mark all as read",
  });
  const clearSearchLabel = intl.formatMessage({
    id: "dashboard.widgets.updates.clearSearch",
    defaultMessage: "Clear search",
  });

  const renderEmptyState = () => (
    <EmptyState
      noActions
      customIcon={<IconCircle icon={ClipboardCheck} color="tertiary" size="lg" />}
      title={intl.formatMessage({
        id: "dashboard.widgets.updates.emptyTitle",
        defaultMessage: "No new updates",
      })}
      description={intl.formatMessage({
        id: "dashboard.widgets.updates.emptyDescription",
        defaultMessage: "When you have new updates, they will show up here.",
      })}
    />
  );

  const renderFilteredEmptyState = () => (
    <div className="recent-updates-widget__filtered-empty">
      <IconCircle icon={FileQuestionIcon} color="tertiary" size="lg" />
      <p className="text-sm text-tertiary">
        {trimmedSearchQuery
          ? intl.formatMessage(
              {
                id: "dashboard.widgets.updates.noMatchTitle",
                defaultMessage: 'No results found for "{query}"',
              },
              { query: trimmedSearchQuery },
            )
          : intl.formatMessage({
              id: "dashboard.widgets.updates.noMatchTitleNoQuery",
              defaultMessage: "No results found",
            })}
      </p>
    </div>
  );

  return (
    <Card borderRadius="md" shadow="xs" style={{ height: "100%" }}>
      <div className="recent-updates-widget">
        <div className="recent-updates-widget__header radius-md">
          <div className="recent-updates-widget__top">
            <div className="recent-updates-widget__title">
              <div className="recent-updates-widget__icon">
                <NotificationIcon />
              </div>
              <div className="font-medium text-md text-primary">
                <FormattedMessage
                  id="dashboard.widgetDialog.listWidgetBuilder.recentUpdate"
                  defaultMessage="Recent updates"
                />
              </div>
              {hasUpdates && (
                <Badge type="category-badge" color="gray" size="sm" label={(notifications ?? []).length} />
              )}
            </div>
            {hasLoadedUpdates && (
              <div className="recent-updates-widget__search">
                <label className="sr-only" htmlFor="recent-updates-search">
                  <FormattedMessage id="dashboard.widgets.updates.searchLabel" defaultMessage="Search recent updates" />
                </label>
                <Input
                  id="recent-updates-search"
                  type="text"
                  icon={<SearchIcon />}
                  iconRight={
                    searchQuery ? (
                      <button
                        type="button"
                        className="recent-updates-widget__search-clear"
                        aria-label={clearSearchLabel}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => updateViewState({ searchQuery: "" })}
                      >
                        <ClearIcon />
                      </button>
                    ) : undefined
                  }
                  value={searchQuery}
                  onChange={(event) => updateViewState({ searchQuery: event.target.value })}
                  placeholder={intl.formatMessage({ id: "general.search", defaultMessage: "Search" })}
                />
              </div>
            )}
          </div>

          {hasLoadedUpdates && (
            <div className="recent-updates-widget__tabs-row">
              <div className="recent-updates-widget__tabs">
                <Tabs
                  activeKey={activeTab}
                  style={{ padding: `0px 20px` }}
                  type="underline"
                  onSelect={(key) => updateViewState({ activeTab: key })}
                >
                  {tabOptions(intl).map((option) => (
                    <Tab key={option.value} eventKey={option.value} title={option.label} />
                  ))}
                </Tabs>
              </div>
              <div className="recent-updates-widget__actions">
                <div
                  className={classNames("recent-updates-widget__unread-filter", {
                    "recent-updates-widget__unread-filter--active": showUnreadOnly,
                  })}
                >
                  <Checkbox
                    size="sm"
                    checked={showUnreadOnly}
                    onChange={(event) => updateViewState({ showUnreadOnly: event.target.checked })}
                    label={intl.formatMessage({ id: "dashboard.widgets.updates.unread", defaultMessage: "Unread" })}
                  />
                </div>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="recent-updates-mark-all-tooltip" className="recent-updates-widget-tooltip">
                      {markAllAsReadLabel}
                    </Tooltip>
                  }
                >
                  <span className="recent-updates-widget__mark-all-wrap">
                    <Button
                      size="sm"
                      variant="secondary-gray"
                      className="recent-updates-widget__mark-all"
                      iconLeft={<CheckDoneIcon />}
                      aria-label={markAllAsReadLabel}
                      disabled={isMarkingAsRead || isMarkingAllAsRead || !hasUnreadUpdates}
                      onClick={markAllAsRead}
                    />
                  </span>
                </OverlayTrigger>
              </div>
            </div>
          )}
        </div>

        {showSkeleton && (
          <div className="recent-updates-widget__loading">
            <LoadingSkeleton rows={2} />
          </div>
        )}

        {hasLoadedUpdates && !hasUpdates && !hasActiveFilters && (
          <div className="recent-updates-widget__empty-state">{renderEmptyState()}</div>
        )}

        {hasLoadedUpdates && (hasUpdates || hasActiveFilters) && (
          <div className="recent-updates-widget__content">
            {filteredNotifications.length === 0 ? (
              <div className="d-flex justify-content-center align-items-center h-100">{renderFilteredEmptyState()}</div>
            ) : (
              filteredNotifications.map((item) => {
                const path = getUpdatePath(item);
                const displayReference = item.type === "invoice" ? item.reference : item.shipment_reference;
                const unread = isUnread(item);

                return (
                  <div
                    key={item.id}
                    className={classNames("recent-updates-widget__item", {
                      "recent-updates-widget__item--read": !unread,
                    })}
                  >
                    <a
                      href={path || undefined}
                      className="recent-updates-widget__item-link"
                      aria-label={displayReference}
                      onClick={path ? (event) => handleCurrentTabNavigation(event, item) : undefined}
                    >
                      <IconCircle icon={renderIcon(item.type)} color={unread ? "primary" : "tertiary"} size="md" />
                      <div className="recent-updates-widget__item-body">
                        <div className="recent-updates-widget__item-heading">
                          <div className="recent-updates-widget__item-reference text-sm text-primary">
                            <span
                              className={classNames("recent-updates-widget__item-id", {
                                "font-medium": unread,
                                "font-regular": !unread,
                              })}
                            >
                              {highlightMatchingText(displayReference, searchQuery)}
                            </span>
                            {item.reference_title && (
                              <span className="font-regular">
                                {" | "}
                                {highlightMatchingText(item.reference_title, searchQuery)}
                              </span>
                            )}
                          </div>
                          <div className="recent-updates-widget__item-time text-xs text-tertiary">
                            {formatRelativeTime(item.created, now, intl)}
                          </div>
                        </div>
                        <div
                          className={classNames("recent-updates-widget__description text-sm", {
                            "text-secondary": unread,
                            "text-tertiary": !unread,
                          })}
                        >
                          {highlightMatchingText(item.notification, searchQuery)}
                        </div>
                      </div>
                      {unread && (
                        <span className="sr-only">
                          <FormattedMessage id="dashboard.widgets.updates.unread" defaultMessage="Unread" />
                        </span>
                      )}
                    </a>
                    {path && (
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id={`recent-updates-open-new-tab-${item.id}`} className="recent-updates-widget-tooltip">
                            {intl.formatMessage({
                              id: "dashboard.widgets.updates.openInNewTabTooltip",
                              defaultMessage: "Open in a new tab",
                            })}
                          </Tooltip>
                        }
                      >
                        <a
                          href={path}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="recent-updates-widget__new-tab"
                          aria-label={intl.formatMessage(
                            {
                              id: "dashboard.widgets.updates.openInNewTab",
                              defaultMessage: "Open {reference} in a new tab",
                            },
                            { reference: displayReference },
                          )}
                          onClick={() => markAsRead(item.id)}
                        >
                          <LinkExternalIcon />
                        </a>
                      </OverlayTrigger>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

export default RecentUpdatesWidget;
